import { useState, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Eye, Search, X } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { cn } from "@/lib/cn";

export interface MessageResourceOption {
  id: string;
  title: string;
  category: string;
  description?: string | null;
  searchText?: string;
}

/** Browsing is read-only. Only the explicit footer action changes the draft. */
export function MessageResourcePicker({
  label,
  icon,
  title,
  options,
  currentId,
  disabled,
  loading,
  failed,
  onRetry,
  onSelect,
  renderPreview,
}: {
  label: string;
  icon: ReactNode;
  title: string;
  options: MessageResourceOption[];
  currentId?: string;
  disabled?: boolean;
  loading?: boolean;
  failed?: boolean;
  onRetry?: () => void;
  onSelect: (id: string) => void;
  renderPreview: (id: string) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const search = query.trim().toLocaleLowerCase();
  const filtered = options.filter((option) =>
    [option.title, option.category, option.description, option.searchText]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase()
      .includes(search),
  );
  const selected =
    !loading && !failed
      ? (filtered.find((option) => option.id === selectedId) ?? filtered[0])
      : undefined;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setQuery("");
          setSelectedId(currentId ?? "");
        }
      }}
    >
      <Dialog.Trigger asChild>
        <Button
          type="button"
          variant="secondary"
          className="min-h-44 gap-8"
          disabled={disabled}
        >
          {icon}
          {label}
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-[var(--surface-overlay)]" />
        <Dialog.Content
          // The prescribing composer lives in a Drawer with its own document
          // Escape listener. Close only this picker, not the underlying chat.
          onEscapeKeyDown={(event) => event.stopPropagation()}
          className="fixed left-1/2 top-1/2 z-[111] flex h-[min(76rem,90dvh)] w-[min(100rem,calc(100vw-20px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden border border-line bg-page shadow-modal"
        >
          <header className="flex shrink-0 items-start justify-between border-b border-line-subtle">
            <div className="p-20">
              <Dialog.Title className="text-20 leading-120">
                {title}
              </Dialog.Title>
              <Dialog.Description className="mt-5 text-14 text-ink-secondary">
                Preview a resource, then add it to your reply. Nothing is sent
                yet.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button
                type="button"
                variant="ghost"
                className="m-8 h-44 w-44 shrink-0 p-0"
                aria-label="Close resource picker"
              >
                <X size={20} aria-hidden />
              </Button>
            </Dialog.Close>
          </header>
          <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] sm:grid-cols-[30rem_minmax(0,1fr)] sm:grid-rows-1">
            <section
              aria-label="Resource library"
              className="flex min-h-0 min-w-0 flex-col border-b border-line-subtle bg-raised sm:border-b-0 sm:border-r"
            >
              <div className="relative m-15">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-12 top-14 text-ink-secondary"
                  aria-hidden
                />
                <input
                  type="search"
                  aria-label="Search resources"
                  placeholder="Search resources…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="h-44 w-full border border-line bg-page pl-36 pr-10 text-16 focus-visible:shadow-focus"
                />
              </div>
              <div className="max-h-[16rem] min-h-0 overflow-y-auto px-8 pb-8 sm:max-h-none sm:flex-1">
                {loading ? (
                  <p role="status" className="p-12 type-label-sm">
                    Fetching resources…
                  </p>
                ) : failed ? (
                  <div role="alert" className="p-12 text-14">
                    <p>Resources could not be loaded.</p>
                    <Button
                      type="button"
                      variant="ghost"
                      className="mt-8 min-h-44"
                      onClick={onRetry}
                    >
                      Retry
                    </Button>
                  </div>
                ) : filtered.length === 0 ? (
                  <p role="status" className="p-12 text-14 text-ink-secondary">
                    {search
                      ? "No matching resources. Try another search."
                      : "No resources available yet."}
                  </p>
                ) : (
                  filtered.map((option) => (
                    <button
                      type="button"
                      key={option.id}
                      aria-label={`Preview ${option.title}`}
                      aria-pressed={selected?.id === option.id}
                      onClick={() => setSelectedId(option.id)}
                      className={cn(
                        "mb-5 flex w-full items-start gap-10 border p-12 text-left focus-visible:shadow-focus",
                        selected?.id === option.id
                          ? "border-ink bg-page"
                          : "border-transparent hover:bg-sunken",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block type-label-xs text-ink-secondary">
                          {option.category}
                        </span>
                        <span className="mt-5 block break-words text-14 leading-130">
                          {option.title}
                        </span>
                        {option.description && (
                          <span className="mt-5 block line-clamp-2 text-12 leading-140 text-ink-secondary">
                            {option.description}
                          </span>
                        )}
                      </span>
                      {selected?.id === option.id && (
                        <Eye size={16} className="mt-2 shrink-0" aria-hidden />
                      )}
                    </button>
                  ))
                )}
              </div>
            </section>
            <section
              aria-label="Resource preview"
              className="min-h-0 min-w-0 overflow-y-auto overscroll-contain bg-page"
            >
              {selected ? (
                <div key={selected.id} className="min-h-full">
                  {renderPreview(selected.id)}
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-10 p-25 text-center text-14 text-ink-secondary">
                  <Eye size={28} strokeWidth={1.5} aria-hidden />
                  Select a resource to preview it here.
                </div>
              )}
            </section>
          </div>
          <footer className="flex shrink-0 items-center justify-between gap-10 border-t border-line-subtle p-15">
            <p className="hidden min-w-0 truncate text-12 text-ink-secondary sm:block">
              {selected?.title ?? "No resource selected"}
            </p>
            <div className="ml-auto flex shrink-0 gap-8">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" className="min-h-44">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                type="button"
                className="min-h-44"
                disabled={disabled || !selected}
                onClick={() => {
                  if (!selected || disabled) return;
                  onSelect(selected.id);
                  setOpen(false);
                }}
              >
                Add to draft
              </Button>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
